import { t } from "i18next";

import { DEFAULT_FRAME_RATE } from "../constants/media";

export const DURATION_UNITS = {
  MILLISECONDS: "ms",
  SECONDS: "s",
} as const;

type DurationUnitKeys = keyof typeof DURATION_UNITS;

type DurationUnits = (typeof DURATION_UNITS)[DurationUnitKeys];

interface PrettyDurationsOptions {
  valueUnit: DurationUnits;
  format?: {
    noDays: boolean;
  };
}

interface PrettyDurationsReturnValue {
  sourceDuration?: number;
  fastDuration: string;
  timecodeDuration: string;
  string: {
    milliseconds: string;
    seconds: string;
    minutes: string;
    hours: string;
    days: string;
  };
  number: {
    milliseconds: number;
    seconds: number;
    minutes: number;
    hours: number;
    days: number;
    frames: number;
  };
  mod: {
    seconds: number;
    minutes: number;
    hours: number;
    frames: number;
  };
  humanised: string;
}

export function padZero(number: number): string {
  return number < 10 ? `0${number}` : `${number}`;
}

export function convertMillisecondsDurationToHMS(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hoursStr = padZero(hours);
  const minutesStr = padZero(minutes);
  const secondsStr = padZero(seconds);

  return `${hoursStr}:${minutesStr}:${secondsStr}`;
}

const EMPTY_DURATION = {
  fastDuration: "",
  timecodeDuration: "00:00:00:00",
  sourceDuration: undefined,
  string: {
    milliseconds: "",
    seconds: "",
    minutes: "",
    hours: "",
    days: "",
  },
  number: {
    milliseconds: 0,
    seconds: 0,
    minutes: 0,
    hours: 0,
    days: 0,
    frames: 0,
  },
  mod: {
    seconds: 0,
    minutes: 0,
    hours: 0,
    frames: 0,
  },
  humanised: "",
};

export default function prettyDurations(
  value: number,
  {
    valueUnit,
    format = {
      noDays: false,
    },
  }: PrettyDurationsOptions,
): PrettyDurationsReturnValue {
  if (!value || Number.isNaN(Number(value)) || value === Infinity || value < 0)
    return EMPTY_DURATION;

  const milliseconds =
    valueUnit === DURATION_UNITS.MILLISECONDS ? value : value * 1000;
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(seconds / 3600);
  const days = Math.floor(seconds / 60 / 60 / 24);
  const frames = Math.floor(milliseconds / (1000 / DEFAULT_FRAME_RATE));

  const secondsMod = seconds % 60;
  const minutesMod = minutes % 60;
  const hoursMod = hours;
  const framesMod = Math.floor(
    (milliseconds % 1000) / (1000 / DEFAULT_FRAME_RATE),
  );

  const hoursStr = padZero(hoursMod);
  const minutesStr = padZero(minutesMod);
  const secondsStr = padZero(secondsMod);
  const framesStr = padZero(framesMod);

  const timecodeDuration = `${hoursStr}:${minutesStr}:${secondsStr}:${framesStr}`;

  const millisecondsString = `${milliseconds}${t(
    "editor-utils:millisecondUnit",
  )}`;
  const secondsString = `${seconds}${t("editor-utils:secondUnit")}`;
  const minutesString =
    minutes >= 1 ? `${minutes}${t("editor-utils:minuteUnit")}` : "";
  const hoursString = hours >= 1 ? `${hours}${t("editor-utils:hourUnit")}` : "";
  const daysString = days >= 1 ? `${days}${t("editor-utils:dayUnit")}` : "";

  const secondsStringMod =
    secondsMod >= 1 ? `${secondsMod}${t("editor-utils:secondUnit")}` : "";
  const minutesStringMod =
    minutesMod >= 1 ? `${minutesMod}${t("editor-utils:minuteUnit")}` : "";
  const hoursStringMod =
    hoursMod >= 1 ? `${hoursMod}${t("editor-utils:hourUnit")}` : "";

  const humanised = `${
    format.noDays ? hoursString : `${daysString} ${hoursStringMod}`
  } ${minutesStringMod} ${secondsStringMod}`
    .replace(/\s+/g, " ") // keep only one space between words
    .trim();

  return {
    fastDuration: convertMillisecondsDurationToHMS(milliseconds),
    timecodeDuration,
    sourceDuration: value,
    humanised: humanised || `0${t("editor-utils:secondUnit")}`,
    string: {
      milliseconds: millisecondsString,
      seconds: secondsString,
      minutes: minutesString,
      hours: hoursString,
      days: daysString,
    },
    number: {
      frames,
      milliseconds,
      seconds,
      minutes,
      hours,
      days,
    },
    mod: {
      seconds: secondsMod,
      minutes: minutesMod,
      hours: hoursMod,
      frames: framesMod,
    },
  };
}

export function humaniseSeconds(seconds: number): string {
  return prettyDurations(seconds, { valueUnit: DURATION_UNITS.SECONDS })
    .humanised;
}

export function timeStringToMilliseconds(time: string): {
  value: number;
  number: {
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
  };
} | null {
  const [hours, minutes, seconds, milliseconds] =
    /(\d+):(\d+):(\d+).(\d+)/.exec(time)?.slice(1).map(Number) ?? [];

  if (
    hours === undefined ||
    minutes === undefined ||
    seconds === undefined ||
    milliseconds === undefined
  ) {
    return null;
  }

  return {
    value:
      hours * 3600 * 1000 + minutes * 60 * 1000 + seconds * 1000 + milliseconds,
    number: {
      hours,
      minutes,
      seconds,
      milliseconds,
    },
  };
}

export function timecodeToDuration(timecode: string): {
  seconds: number;
  milliseconds: number;
  millisecondsWithFrames: number;
} {
  const parts = timecode.split(":").map(Number);
  if (parts.length !== 4 || parts.some((part) => Number.isNaN(part))) {
    throw new Error(`Format invalide : ${timecode}`);
  }

  const [hh = 0, mm = 0, ss = 0, ff = 0] = parts;

  const seconds = hh * 3600 + mm * 60 + ss;

  const milliseconds = seconds * 1000;

  const frameMs = Math.floor((ff / DEFAULT_FRAME_RATE) * 1000);

  return {
    seconds,
    milliseconds,
    millisecondsWithFrames: milliseconds + frameMs,
  };
}
